import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/contact');
      if (response.data.success) {
        setContacts(response.data.contacts);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Update contact status
  const handleStatusUpdate = async (contactId, newStatus) => {
    try {
      const response = await axios.patch(`/api/contact/${contactId}/status`, {
        status: newStatus
      });

      if (response.data.success) {
        setContacts(contacts.map(contact => 
          contact.id === contactId ? response.data.contact : contact
        ));
        toast.success('Status updated successfully');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  // Delete contact
  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      const response = await axios.delete(`/api/contact/${contactId}`);
      if (response.data.success) {
        setContacts(contacts.filter(contact => contact.id !== contactId));
        toast.success('Contact deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge color
  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500',
      read: 'bg-yellow-500',
      replied: 'bg-green-500',
      archived: 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Contact Messages</h1>
        <p className="text-gray-400">Manage and respond to contact form submissions</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      ) : (
        <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {contacts.map((contact) => (
                  <motion.tr
                    key={contact.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-white/5"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {formatDate(contact.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {contact.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {contact.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {contact.subject}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(contact.status)} text-white`}>
                        {contact.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => setSelectedContact(contact)}
                        className="text-primary hover:text-primary/80"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(contact.id, 'read')}
                        className="text-yellow-500 hover:text-yellow-400"
                        disabled={contact.status === 'read'}
                      >
                        Mark Read
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(contact.id, 'replied')}
                        className="text-green-500 hover:text-green-400"
                        disabled={contact.status === 'replied'}
                      >
                        Mark Replied
                      </button>
                      <button
                        onClick={() => handleDelete(contact.id)}
                        className="text-red-500 hover:text-red-400"
                      >
                        Delete
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Contact Details Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-white">Contact Details</h2>
                <button
                  onClick={() => setSelectedContact(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-400 mb-1">Name</h3>
                    <p className="text-white text-lg">{selectedContact.name}</p>
                  </div>

                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-400 mb-1">Email</h3>
                    <p className="text-white text-lg break-all">{selectedContact.email}</p>
                  </div>

                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-400 mb-1">Phone Number</h3>
                    <p className="text-white text-lg">
                      {selectedContact.phone_number || 'Not provided'}
                    </p>
                  </div>

                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-400 mb-1">Company Name</h3>
                    <p className="text-white text-lg">
                      {selectedContact.company_name || 'Not provided'}
                    </p>
                  </div>

                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-400 mb-1">Status</h3>
                    <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${getStatusColor(selectedContact.status)} text-white`}>
                      {selectedContact.status}
                    </span>
                  </div>

                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-400 mb-1">Received On</h3>
                    <p className="text-white text-lg">{formatDate(selectedContact.createdAt)}</p>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-400 mb-1">Subject</h3>
                    <p className="text-white text-lg">{selectedContact.subject}</p>
                  </div>

                  {selectedContact.services_interested && selectedContact.services_interested.length > 0 && (
                    <div className="bg-gray-800/50 p-4 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-400 mb-2">Services Interested</h3>
                      <div className="space-y-1">
                        {Array.isArray(selectedContact.services_interested) 
                          ? selectedContact.services_interested.map((service, index) => (
                              <div key={index} className="bg-gray-700/50 px-3 py-2 rounded text-white">
                                {service}
                              </div>
                            ))
                          : (() => {
                              try {
                                // Try to parse as JSON
                                const parsedServices = JSON.parse(selectedContact.services_interested);
                                return Array.isArray(parsedServices) 
                                  ? parsedServices.map((service, index) => (
                                      <div key={index} className="bg-gray-700/50 px-3 py-2 rounded text-white">
                                        {service}
                                      </div>
                                    ))
                                  : <div className="bg-gray-700/50 px-3 py-2 rounded text-white">{selectedContact.services_interested}</div>;
                              } catch (e) {
                                // If parsing fails, display as a plain string
                                return <div className="bg-gray-700/50 px-3 py-2 rounded text-white">{selectedContact.services_interested}</div>;
                              }
                            })()
                        }
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-400 mb-1">Message</h3>
                    <p className="text-white text-lg whitespace-pre-wrap">{selectedContact.message}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-end space-x-3">
                <button
                  onClick={() => handleStatusUpdate(selectedContact.id, 'read')}
                  disabled={selectedContact.status === 'read'}
                  className={`px-4 py-2 rounded-lg ${
                    selectedContact.status === 'read'
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  } text-white transition-colors`}
                >
                  Mark as Read
                </button>
                <button
                  onClick={() => handleStatusUpdate(selectedContact.id, 'replied')}
                  disabled={selectedContact.status === 'replied'}
                  className={`px-4 py-2 rounded-lg ${
                    selectedContact.status === 'replied'
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white transition-colors`}
                >
                  Mark as Replied
                </button>
                <button
                  onClick={() => {
                    handleStatusUpdate(selectedContact.id, 'archived');
                    setSelectedContact(null);
                  }}
                  disabled={selectedContact.status === 'archived'}
                  className={`px-4 py-2 rounded-lg ${
                    selectedContact.status === 'archived'
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-gray-600 hover:bg-gray-700'
                  } text-white transition-colors`}
                >
                  Archive
                </button>
                <button
                  onClick={() => setSelectedContact(null)}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
